import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCandidateRecentNotes } from '@/hooks/use-candidates';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { CandidateNoteBody } from './candidate-note-body';

interface CandidateRecentNotesProps {
  candidateId: string;
}

function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || '?';
}

export function CandidateRecentNotes({ candidateId }: CandidateRecentNotesProps) {
  const { data, isLoading } = useCandidateRecentNotes(candidateId);
  const notes = data?.data ?? [];

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-medium">Recent notes</h2>
        <Link
          to="/recruitment/candidates/$id/notes"
          params={{ id: candidateId }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Open all →
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet.</p>
      ) : (
        <ul className="space-y-3">
          {notes.map((note) => {
            const isFromPF = note.source === 'peopleforce';
            let authorName = 'System';
            if (note.author) {
              authorName =
                `${note.author.firstName} ${note.author.lastName}`.trim() || 'Unknown';
            } else if (isFromPF) {
              authorName = note.pfAuthorName || note.pfAuthorEmail || 'PeopleForce user';
            }
            return (
              <li key={note.id} className="flex items-start gap-2.5">
                <Avatar className="h-7 w-7 shrink-0 rounded-full">
                  {note.author?.avatarUrl ? (
                    <AvatarImage src={note.author.avatarUrl} alt={authorName} />
                  ) : null}
                  <AvatarFallback className="rounded-full text-[10px]">
                    {note.author ? initials(note.author.firstName, note.author.lastName) : isFromPF ? 'PF' : 'S'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground">
                    {authorName}
                    {isFromPF && (
                      <span
                        className="ml-1.5 rounded bg-hygge/10 px-1 text-[9px] font-medium uppercase tracking-wide text-hygge"
                        title="Imported from PeopleForce"
                      >
                        PF
                      </span>
                    )}
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      · {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
                    </span>
                  </p>
                  <div className="line-clamp-2 text-xs text-muted-foreground [&_p]:my-0">
                    <CandidateNoteBody body={note.body} className="!text-xs" />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}